import React, { createContext, useContext, useState, ReactNode, useMemo } from "react";

interface NavigationBlockingContextType {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  showWarningModal: boolean;
  setShowWarningModal: (value: boolean) => void;
  pendingNavigation: string | null;
  setPendingNavigation: (value: string | null) => void;
  onDiscardChanges: (() => void) | null;
  setOnDiscardChanges: (callback: (() => void) | null) => void;
}

const NavigationBlockingContext = createContext<NavigationBlockingContextType | undefined>(undefined);

export const NavigationBlockingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [onDiscardChanges, setOnDiscardChanges] = useState<(() => void) | null>(null);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      hasUnsavedChanges,
      setHasUnsavedChanges,
      showWarningModal,
      setShowWarningModal,
      pendingNavigation,
      setPendingNavigation,
      onDiscardChanges,
      setOnDiscardChanges,
    }),
    [hasUnsavedChanges, showWarningModal, pendingNavigation, onDiscardChanges]
  );

  return (
    <NavigationBlockingContext.Provider value={contextValue}>
      {children}
    </NavigationBlockingContext.Provider>
  );
};

export const useNavigationBlocking = () => {
  const context = useContext(NavigationBlockingContext);
  if (context === undefined) {
    throw new Error("useNavigationBlocking must be used within a NavigationBlockingProvider");
  }
  return context;
};

